/* ==========================================================================
   BuildDirect — Fill-to-cap sample suggestions
   --------------------------------------------------------------------------
   The hand for sections/bd-sample-suggestions.liquid. That section decides
   which floors qualify and renders the cards; this element fetches them
   through the Section Rendering API and adds the one the shopper picks.
   Nothing here knows what a sample is.

   The placeholder is rendered by snippets/bd-sample-suggestions-slot.liquid
   inside the regions the cart re-renders over AJAX, so every cart change
   produces a fresh element with a fresh answer to "how many more". Fetching
   waits until the element is on screen: a closed drawer on a collection page
   must not cost a recommendations request on every page load.

   Two URLs. data-url asks for Shopify's related products of the first sample
   in the cart; if that comes back with no eligible card, data-fallback-url
   (a collection chosen in theme settings) is tried. A failed request hides
   the element rather than showing an empty box.

   After an add the cart is re-rendered the way the rest of the theme does it:
   the drawer through its own refresh event; the cart page through
   <cart-items>; and 'bd:cart:changed' for anything else listening, which is
   how sections/bd-samples-checkout.liquid re-renders its list.

   Events pushed to dataLayer:

   | Event                          | Fires on                                |
   |--------------------------------|-----------------------------------------|
   | cart_sample_suggestions_view   | cards rendered (carries how many)        |
   | cart_sample_suggestion_add     | a suggested sample added to the cart     |
   ========================================================================== */

(function () {
  if (customElements.get('bd-sample-suggestions')) return;

  var routes = (window.theme && window.theme.routes) || {};
  var CART_ADD = (routes.cartAdd || '/cart/add') + '.js';

  function push(event, payload) {
    window.dataLayer = window.dataLayer || [];
    var data = { event: event };
    for (var k in payload || {}) if (Object.prototype.hasOwnProperty.call(payload, k)) data[k] = payload[k];
    window.dataLayer.push(data);
  }

  function fetchCards(url) {
    return fetch(url, { headers: { Accept: 'text/html' } })
      .then(function (response) {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then(function (html) {
        return new DOMParser().parseFromString(html, 'text/html').querySelector('[data-bd-suggestions]');
      })
      .catch(function () {
        return null;
      });
  }

  function count(el) {
    return el ? parseInt(el.getAttribute('data-bd-suggestions-count'), 10) || 0 : 0;
  }

  class BdSampleSuggestions extends HTMLElement {
    connectedCallback() {
      if (this.armed) return;
      this.armed = true;
      this.addEventListener('click', this.handleClick.bind(this));

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.observer.disconnect();
            this.load();
          },
          { rootMargin: '200px' }
        );
        this.observer.observe(this);
      } else {
        this.load();
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    async load() {
      if (this.loaded) return;
      this.loaded = true;

      let cards = await fetchCards(this.dataset.url);
      if (count(cards) === 0 && this.dataset.fallbackUrl) {
        cards = (await fetchCards(this.dataset.fallbackUrl)) || cards;
      }

      if (count(cards) === 0) {
        this.hidden = true;
        return;
      }

      this.innerHTML = cards.outerHTML;
      if (window.initLazyImages) window.initLazyImages();

      push('cart_sample_suggestions_view', {
        suggestions_shown: count(cards),
        samples_remaining: parseInt(this.dataset.remaining, 10) || 0
      });
    }

    handleClick(evt) {
      const btn = evt.target.closest('[data-bd-suggestion-add]');
      if (!btn || btn.disabled) return;
      evt.preventDefault();

      const variantId = Number(btn.getAttribute('data-bd-suggestion-add'));
      if (!variantId) return;

      const error = this.querySelector('[data-bd-suggestions-error]');
      if (error) error.hidden = true;
      btn.disabled = true;
      btn.classList.add('is-loading');

      window.BDSampleQuantity.add([{ id: variantId, quantity: 1 }])
        .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
        .then(({ ok, data }) => {
          if (!ok) throw new Error(data.description || data.message || 'Could not add that sample.');

          push('cart_sample_suggestion_add', {
            sample_variant_id: String(variantId),
            sample_title: btn.getAttribute('data-bd-suggestion-title') || '',
            samples_remaining_before: parseInt(this.dataset.remaining, 10) || 0
          });

          const card = btn.closest('[data-bd-suggestion]');
          if (card) card.classList.add('is-added');
          btn.textContent = 'Added';

          this.refreshCart();
        })
        .catch((err) => {
          btn.disabled = false;
          btn.classList.remove('is-loading');
          if (error) {
            error.textContent = err && err.message ? err.message : 'Could not add that sample. Please try again.';
            error.hidden = false;
          }
        });
    }

    /* Re-render the cart the way the surface expects. The drawer has an event
       for it; the cart page has <cart-items>; anything else (the samples
       checkout page) listens for bd:cart:changed. */
    refreshCart() {
      document.dispatchEvent(new CustomEvent('bd:cart:changed', { detail: { source: 'sample-suggestion' } }));

      if (document.getElementById('cart-drawer')) {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
        return;
      }

      const cartItems = document.querySelector('cart-items');
      if (cartItems && typeof cartItems.refreshCartItems === 'function') cartItems.refreshCartItems();
    }
  }

  customElements.define('bd-sample-suggestions', BdSampleSuggestions);
})();

